import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Tooltip, Popup, CircleMarker, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './Messages.css';
import './DashboardInfo.css';
import './MapHud.css';
import './ReceiverMarker.css';
import { getAccessToken } from '../utils/auth';
import { compactNumber } from '../utils/formatNumber';
import { getAcarsMessages, getAcarsAircraft, getAcarsStats } from '../utils/httpApi';
import { getTileLayerConfig, getMapFilter } from '../utils/mapStyles';
import { useSettings } from '../contexts/SettingsContext';
import { FlagIcon, AirlineLogo, AircraftTypeDisplay, extractAirlineCode } from './AircraftTableComponents';
import { getCountryFromHex, isMilitaryAircraft } from '../utils/icaoCountryMap';
import { getCountryFromRegistration } from '../utils/registrationCountryMap';
import { getFrequencyTooltip } from '../utils/frequencyMap';
import { getAircraftSpritePosition, getAircraftSizeMultiplier } from '../utils/aircraftSpriteMap';
import { getAirlineLogo, hasAirlineLogo, getMilitaryLogo, hasMilitaryLogo, useAirlineLogoMapVersion } from '../utils/airlineLogoMap';
import spriteImage from '../assets/BelugaProject/beluga_sprites.png';

// Green diamond icon for ACARS aircraft (fallback when no type/heading)
const acarsIcon = L.divIcon({
  className: 'acars-marker-icon',
  html: `<svg width="16" height="16" viewBox="0 0 16 16"><polygon points="8,1 15,8 8,15 1,8" fill="#16a34a" stroke="#fff" stroke-width="1.5"/></svg>`,
  iconSize: [16, 16],
  iconAnchor: [8, 8]
});

// Larger blue diamond for selected ACARS aircraft (fallback)
const acarsIconSelected = L.divIcon({
  className: 'acars-marker-icon selected',
  html: `<svg width="22" height="22" viewBox="0 0 22 22"><polygon points="11,1 21,11 11,21 1,11" fill="#3b82f6" stroke="#fff" stroke-width="2"/></svg>`,
  iconSize: [22, 22],
  iconAnchor: [11, 11]
});

// Sprite icon cache to avoid recreating on every render
const spriteIconCache = new Map();

/**
 * Create a sprite-based aircraft icon rotated by heading.
 * Falls back to diamond icon when no aircraft type or heading.
 */
function getAcarsAircraftIcon(aircraftType, heading, isSelected) {
  if (!aircraftType || heading == null) {
    return isSelected ? acarsIconSelected : acarsIcon;
  }
  const sprite = getAircraftSpritePosition(aircraftType);
  if (!sprite) {
    return isSelected ? acarsIconSelected : acarsIcon;
  }

  const cacheKey = `${aircraftType}:${Math.round(heading)}:${isSelected ? 1 : 0}`;
  if (spriteIconCache.has(cacheKey)) return spriteIconCache.get(cacheKey);

  const sizeMultiplier = getAircraftSizeMultiplier(aircraftType);
  const baseSize = isSelected ? 38 : 30;
  const size = Math.round(baseSize * sizeMultiplier);
  const half = Math.round(size / 2);
  const spriteSize = 64;
  const bgX = -(sprite.col * spriteSize);
  const bgY = -(sprite.row * spriteSize);
  const color = isSelected ? '#3b82f6' : '#16a34a';

  const icon = L.divIcon({
    className: 'acars-sprite-icon',
    html: `<div style="width:${size}px;height:${size}px;position:relative;transform:rotate(${heading}deg);">` +
      `<div style="width:${size}px;height:${size}px;background:url(${spriteImage}) ${bgX * size / spriteSize}px ${bgY * size / spriteSize}px / ${size * 8}px ${size * 8}px no-repeat;filter:brightness(0) saturate(100%) drop-shadow(0 0 1px rgba(0,0,0,0.5));opacity:0.3;position:absolute;top:1px;left:1px;"></div>` +
      `<div style="width:${size}px;height:${size}px;background:url(${spriteImage}) ${bgX * size / spriteSize}px ${bgY * size / spriteSize}px / ${size * 8}px ${size * 8}px no-repeat;filter:brightness(0) saturate(100%) invert(1);position:absolute;top:0;left:0;"></div>` +
    `</div>`,
    iconSize: [size, size],
    iconAnchor: [half, half]
  });

  spriteIconCache.set(cacheKey, icon);
  if (spriteIconCache.size > 500) {
    // Evict oldest entries
    const keys = spriteIconCache.keys();
    for (let i = 0; i < 100; i++) spriteIconCache.delete(keys.next().value);
  }
  return icon;
}

// Receiver antenna icon — matches ADS-B ReceiverMarker
const receiverIcon = L.divIcon({
  html: `<img src="/antenna.svg" style="width: 24px; height: 24px;" />`,
  className: 'receiver-marker',
  iconSize: [24, 24],
  iconAnchor: [12, 12],
  popupAnchor: [0, -12]
});

/** ACARS aircraft stay visible for 20 minutes (much lower message rate than ADS-B) */
const ACARS_AIRCRAFT_TIMEOUT_MS = 20 * 60 * 1000;

/** Aircraft with positions stay on the map for 5 minutes after last position message */
const ACARS_POSITION_MAP_TIMEOUT_MS = 20 * 60 * 1000; // Same as aircraft list timeout — ACARS messages are infrequent

/** Parse a timestamp string as UTC. Backend sends LocalDateTime without 'Z' suffix. */
function parseUtcTimestamp(ts) {
  if (!ts) return 0;
  if (typeof ts === 'number') return ts;
  return new Date(ts.endsWith('Z') ? ts : ts + 'Z').getTime();
}

/** Derive message type from frequency */
function getMessageType(freqMhz) {
  if (!freqMhz) return '-';
  if (freqMhz >= 136.0 && freqMhz < 137.5) return 'VDL2';
  if (freqMhz >= 129.0 && freqMhz < 133.0) return 'ACARS';
  return 'ACARS';
}

/** OOOI labels indicating ground events */
const GROUND_LABELS = new Set(['QB', 'QD', 'QE', '16', 'QA', 'QN', '_d', '17']);
/** OOOI labels indicating airborne events */
const AIRBORNE_LABELS = new Set(['QC']);

/**
 * Determine airborne/ground status from available data.
 * Returns 'airborne', 'ground', or null (unknown).
 */
function getAirGroundStatus(msg, enrichedAc) {
  // 1. VDL2 AVLC air/ground bit (most reliable for VDL2 messages)
  if (msg?.airGround) {
    return msg.airGround === 'On ground' ? 'ground' : 'airborne';
  }
  // 2. Flight level from ACARS position report
  if (msg?.flightLevel != null) {
    return msg.flightLevel > 0 ? 'airborne' : 'ground';
  }
  // 3. OOOI label
  if (msg?.label) {
    if (AIRBORNE_LABELS.has(msg.label)) return 'airborne';
    if (GROUND_LABELS.has(msg.label)) return 'ground';
  }
  // 4. ADS-B enriched altitude (alt_baro field if passed through)
  if (enrichedAc?.alt_baro != null) {
    return enrichedAc.alt_baro === 'ground' ? 'ground' : 'airborne';
  }
  return null;
}

/**
 * Determine airborne/ground status for an aircraft summary row.
 * Uses flight level from the most recent position message.
 */
function getAircraftAirGroundStatus(ac, enrichedAc) {
  if (ac?.flightLevel != null) {
    return ac.flightLevel > 0 ? 'airborne' : 'ground';
  }
  // Check last label for OOOI ground/airborne status
  if (ac?.lastLabel) {
    if (AIRBORNE_LABELS.has(ac.lastLabel)) return 'airborne';
    if (GROUND_LABELS.has(ac.lastLabel)) return 'ground';
  }
  if (enrichedAc?.alt_baro != null) {
    return enrichedAc.alt_baro === 'ground' ? 'ground' : 'airborne';
  }
  return null;
}

/** Location pin icon for messages with position data */
const LocationIcon = ({ hasPosition, title }) => {
  if (!hasPosition) return <span className="location-icon empty">—</span>;
  return (
    <span className="location-icon has-pos" title={title || 'Has position'}>
      <svg width="12" height="14" viewBox="0 0 12 14">
        <path d="M6 0C2.7 0 0 2.5 0 5.6 0 9.8 6 14 6 14s6-4.2 6-8.4C12 2.5 9.3 0 6 0zm0 7.6c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z" fill="var(--accent-secondary, #16a34a)"/>
      </svg>
    </span>
  );
};

/** Colored badge for airborne / on-ground status */
const AirGroundIcon = ({ status }) => {
  if (status === 'airborne') {
    return <span className="ag-badge airborne" title="Airborne">A</span>;
  }
  if (status === 'ground') {
    return <span className="ag-badge ground" title="On Ground">G</span>;
  }
  return <span className="ag-badge unknown" title="Unknown">?</span>;
};

/** Fly-to component: pans the map to a given position when it changes */
function MapFlyTo({ position }) {
  const map = useMap();
  useEffect(() => {
    if (position) {
      map.flyTo(position, Math.max(map.getZoom(), 8), { duration: 0.8 });
    }
  }, [map, position]);
  return null;
}

/** Minimum auto-zoom radius in km (100km from receiver) */
const MIN_AUTO_ZOOM_RADIUS_KM = 100;

/**
 * Auto-zoom controller for the ACARS map.
 * Modes: 'none' | 'aircraft' | 'airport'
 * - aircraft: fits all ACARS positions + receiver within bounds (min 100km radius)
 * - airport: zooms to receiver's configured airport area
 */
function AutoZoomController({ mode, receiverLocation, aircraftPositions, selectedMessagePos }) {
  const map = useMap();
  const lastZoomRef = useRef(0);

  useEffect(() => {
    if (mode === 'none') return;
    // Don't auto-zoom more than every 5 seconds
    const now = Date.now();
    if (now - lastZoomRef.current < 5000) return;

    if (mode === 'aircraft') {
      // Collect all points: receiver + aircraft positions
      const points = [];
      if (receiverLocation) points.push(receiverLocation);
      if (aircraftPositions && aircraftPositions.length > 0) {
        aircraftPositions.forEach(ac => {
          if (ac.latitude && ac.longitude) points.push([ac.latitude, ac.longitude]);
        });
      }
      // Also include selected message position
      if (selectedMessagePos) {
        points.push([selectedMessagePos.lat, selectedMessagePos.lng]);
      }

      if (points.length === 0) return;

      // Calculate bounds
      const bounds = L.latLngBounds(points);

      // Enforce minimum 100km radius from center
      const center = bounds.getCenter();
      // 1 degree latitude ≈ 111km
      const minDeg = MIN_AUTO_ZOOM_RADIUS_KM / 111;
      const expandedBounds = L.latLngBounds(
        [Math.min(bounds.getSouth(), center.lat - minDeg), Math.min(bounds.getWest(), center.lng - minDeg)],
        [Math.max(bounds.getNorth(), center.lat + minDeg), Math.max(bounds.getEast(), center.lng + minDeg)]
      );

      // Add 10% buffer
      map.flyToBounds(expandedBounds.pad(0.1), { duration: 1, maxZoom: 14 });
      lastZoomRef.current = now;
    }

    if (mode === 'airport') {
      // Zoom to airport area around the receiver (±2km)
      if (!receiverLocation) return;
      const airportRadiusDeg = 2 / 111; // ~2km
      const bounds = L.latLngBounds(
        [receiverLocation[0] - airportRadiusDeg, receiverLocation[1] - airportRadiusDeg],
        [receiverLocation[0] + airportRadiusDeg, receiverLocation[1] + airportRadiusDeg]
      );
      map.flyToBounds(bounds, { duration: 1, maxZoom: 16 });
      lastZoomRef.current = now;
    }
  }, [map, mode, receiverLocation, aircraftPositions, selectedMessagePos]);

  return null;
}

/** Rich ACARS aircraft popup for map markers — mirrors ADS-B AircraftInfoPopup style */
function AcarsAircraftPopup({ ac, enriched }) {
  useAirlineLogoMapVersion();
  const reg = enriched?.registration || cleanRegistration(ac.address);
  const countryCode = enriched?.countryCode || getCountryFromRegistration(ac.address || enriched?.registration) || getCountryFromHex(ac.icao24);
  const aircraftType = enriched?.aircraftType;
  const airlineCode = enriched?.airlineCode;
  const airlineName = enriched?.airlineName;
  const military = enriched?.military;
  const flight = ac.flight || enriched?.flight;

  // Aircraft sprite icon
  let aircraftIconStyle = null;
  if (aircraftType) {
    const spritePos = getAircraftSpritePosition(aircraftType);
    const sizeMultiplier = getAircraftSizeMultiplier(aircraftType);
    const iconSize = Math.max(28, Math.round(40 * sizeMultiplier));
    const bgPosX = -(spritePos.col * iconSize);
    const bgPosY = -(spritePos.row * iconSize);
    aircraftIconStyle = {
      width: `${iconSize}px`, height: `${iconSize}px`,
      backgroundSize: `${iconSize * 8}px auto`,
      backgroundPosition: `${bgPosX}px ${bgPosY}px`,
      backgroundImage: `url(${spriteImage})`,
      backgroundRepeat: 'no-repeat',
      display: 'inline-block',
      transform: 'rotate(90deg)',
      filter: 'brightness(0) invert(1)',
      flexShrink: 0,
    };
  }

  // Airline / military logo
  let logoElement = null;
  if (military && countryCode && hasMilitaryLogo(countryCode)) {
    const logo = getMilitaryLogo(countryCode, airlineName);
    logoElement = <img src={logo} alt="Military" className="acars-popup-logo" onError={e => e.target.style.display='none'} />;
  } else if (airlineCode && hasAirlineLogo(airlineCode)) {
    const logo = getAirlineLogo(airlineCode, 'icon');
    logoElement = <img src={logo} alt={airlineCode} className="acars-popup-logo" onError={e => e.target.style.display='none'} />;
  }

  const agStatus = ac.flightLevel != null ? (ac.flightLevel > 0 ? 'airborne' : 'ground') : null;

  return (
    <div className="acars-popup">
      <div className="acars-popup-header">
        {aircraftIconStyle && <div style={aircraftIconStyle}></div>}
        <div className="acars-popup-header-info">
          <div className="acars-popup-reg">{reg}</div>
          {ac.icao24 && <div className="acars-popup-hex">ICAO: {ac.icao24.toUpperCase()}</div>}
        </div>
      </div>
      <div className="acars-popup-body">
        {(airlineName || logoElement) && (
          <div className="acars-popup-airline">
            {logoElement}
            <strong>{airlineName || airlineCode}</strong>
          </div>
        )}
        {(enriched?.aircraftDescription || aircraftType) && (
          <div className="acars-popup-type">{(enriched?.aircraftDescription || aircraftType)?.replace(/,\s*/g, ' ')}</div>
        )}
        {countryCode && (
          <div className="acars-popup-row">
            <img src={`https://flagcdn.com/16x12/${countryCode.toLowerCase()}.png`} width="16" height="12" alt="" style={{borderRadius:2,border:'1px solid #ddd'}} onError={e => e.target.style.display='none'} />
            <span>{enriched?.country || countryCode.toUpperCase()}</span>
          </div>
        )}
        {flight && (
          <div className="acars-popup-row">
            <span className="acars-popup-label">Flight:</span>
            <span className="acars-popup-value">{flight}</span>
          </div>
        )}
        {ac.flightLevel != null && (
          <div className="acars-popup-row">
            <span className="acars-popup-label">Altitude:</span>
            <span className="acars-popup-value">FL{ac.flightLevel} ({(ac.flightLevel * 100).toLocaleString()} ft)</span>
          </div>
        )}
        {agStatus && (
          <div className="acars-popup-row">
            <span className="acars-popup-label">Status:</span>
            <AirGroundIcon status={agStatus} />
          </div>
        )}
        {ac.latitude && ac.longitude && (
          <div className="acars-popup-row">
            <span className="acars-popup-label">Position:</span>
            <span className="acars-popup-value">{ac.latitude.toFixed(4)}°, {ac.longitude.toFixed(4)}°</span>
          </div>
        )}
        <div className="acars-popup-row">
          <span className="acars-popup-label">Messages:</span>
          <span className="acars-popup-value">{ac.messageCount}</span>
        </div>
        {ac.lastLabel && (
          <div className="acars-popup-row">
            <span className="acars-popup-label">Last Label:</span>
            <span className="acars-popup-value"><span className="label-code">{ac.lastLabel}</span>{ac.lastLabelDescription && <span className="label-desc"> {ac.lastLabelDescription}</span>}</span>
          </div>
        )}
      </div>
    </div>
  );
}

/** Strip leading dot from ACARS registration address */
function cleanRegistration(address) {
  if (!address) return '-';
  // Strip all leading dots (some VDL2 messages have . others ..)
  return address.replace(/^\.+/, '') || '-';
}

/** Look up icao24 in live ADS-B data for enrichment */
function enrichFromAdsb(icao24, aircraftData) {
  if (!icao24 || !aircraftData) return null;
  const hex = icao24.toUpperCase();
  const ac = aircraftData[hex] || aircraftData[hex.toLowerCase()];
  if (!ac) return null;
  const callsign = ac.flight?.trim() || ac.callsign;
  // Backend serializes fields with short names: r, t, desc, airline_icao, airline_name, country_code
  let airlineCode = ac.airline_icao || ac.airlineCode;
  let airlineName = ac.airline_name || ac.airlineName;
  // Extract airline from callsign if not already known (not when it is the registration)
  if (!airlineCode && callsign) {
    airlineCode = extractAirlineCode(callsign, { registration: ac.r || ac.registration, military: ac.military });
  }
  return {
    registration: ac.r || ac.registration,
    callsign,
    aircraftType: ac.t || ac.aircraftType,
    aircraftDescription: ac.desc || ac.aircraftDescription,
    airlineCode,
    airlineName,
    countryCode: ac.country_code || ac.countryCode,
    country: ac.country,
    military: ac.military === true || (ac.category != null && isMilitaryAircraft(ac.category))
  };
}

/**
 * Build enrichment from backend-provided fields on an ACARS message.
 * The backend enriches from the aircraft_registration DB before broadcast.
 * Returns enrichment object or null if no backend data present.
 */
function enrichFromBackendMsg(msg) {
  if (!msg) return null;
  // Backend enrichment fields: registration, aircraftType, ownerOperator, military
  // Also flight identification: carrierIcao, carrierIata, carrierName, flight, flightNumber
  if (!msg.registration && !msg.aircraftType && !msg.ownerOperator && !msg.carrierIcao) return null;
  // Use ICAO carrier code for airline logo lookup (3-letter code works with AirlineLogo component)
  const airlineCode = msg.carrierIcao || null;
  const airlineName = msg.carrierName || msg.ownerOperator || null;
  return {
    registration: msg.registration || null,
    aircraftType: msg.aircraftType || null,
    aircraftDescription: null,
    airlineCode,
    airlineName,
    countryCode: msg.countryCode || null,
    country: null,
    military: msg.military === true,
    icao24: msg.icao24 || null,
    flight: msg.flight || null,
    carrierIata: msg.carrierIata || null,
    flightNumber: msg.flightNumber || null,
  };
}

/** Reverse lookup: find aircraft data by registration (when ICAO24 is missing) */
function enrichFromRegistration(registration, regLookupMap) {
  if (!registration || !regLookupMap) return null;
  const clean = registration.replace(/^\.+/, '');
  return regLookupMap[clean.toUpperCase()] || null;
}

/** Get receiver status class */
function getStatusClass(receiver) {
  if (!receiver) return 'offline';
  if (receiver.status) {
    switch (receiver.status) {
      case 'ACTIVE': return 'online';
      case 'CONNECTED': return 'connected';
      case 'OFFLINE': return 'offline';
      default: break;
    }
  }
  return receiver.isActive ? 'online' : 'offline';
}

/** Get receiver status label */
function getStatusLabel(receiver) {
  const status = getStatusClass(receiver);
  switch (status) {
    case 'online': return 'Online';
    case 'connected': return 'Connected';
    default: return 'Offline';
  }
}

function Messages({ receivers = [], viewerReceivers = [], selectedReceiverUuid, acarsMessages = [], onReceiverChange, aircraftData = {} }) {
  const { settings } = useSettings();
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [viewMode, setViewMode] = useState('live'); // 'live' | 'history' | 'interesting'
  const [selectedAircraft, setSelectedAircraft] = useState(null);
  const [expandedMessage, setExpandedMessage] = useState(null);
  const [selectedMessagePos, setSelectedMessagePos] = useState(null); // { lat, lng, reg, type, time }
  const [historyMessages, setHistoryMessages] = useState([]);
  const [aircraftList, setAircraftList] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [localReceiverUuid, setLocalReceiverUuid] = useState(null);
  const [acarsAutoZoom, setAcarsAutoZoom] = useState(() => localStorage.getItem('acarsAutoZoom') || 'aircraft');
  const [seedMessages, setSeedMessages] = useState([]); // Historical messages loaded from DB on mount
  const seedLoadedRef = useRef({}); // Track which receivers have been seeded { uuid: true }
  const tableContainerRef = useRef(null);

  // All receivers combined
  const allReceivers = useMemo(() => [...receivers, ...viewerReceivers], [receivers, viewerReceivers]);

  // Build a receiverUuid -> receiverName map for display
  const receiverNameMap = useMemo(() => {
    const map = {};
    allReceivers.forEach(r => {
      map[r.receiverUuid] = r.receiverName || r.displayName || r.receiverUuid;
    });
    return map;
  }, [allReceivers]);

  // Determine if we're showing all receivers or a specific one
  const showAllReceivers = !localReceiverUuid || localReceiverUuid === 'ALL';

  // Get the effective receiver UUID for API queries (first receiver if showing all)
  const effectiveReceiverUuid = useMemo(() => {
    if (localReceiverUuid && localReceiverUuid !== 'ALL') {
      return localReceiverUuid;
    }
    if (allReceivers.length > 0) {
      return allReceivers[0].receiverUuid;
    }
    return null;
  }, [localReceiverUuid, allReceivers]);

  // Initialize local receiver from parent selection
  useEffect(() => {
    if (selectedReceiverUuid && selectedReceiverUuid !== 'ALL_RECEIVERS') {
      setLocalReceiverUuid(selectedReceiverUuid);
    } else {
      setLocalReceiverUuid(allReceivers.length > 1 ? 'ALL' : (allReceivers[0]?.receiverUuid || null));
    }
  }, [selectedReceiverUuid, allReceivers]);

  // Get the selected receiver object for status display
  const selectedReceiver = useMemo(() => {
    if (showAllReceivers) return null;
    return allReceivers.find(r => r.receiverUuid === localReceiverUuid);
  }, [showAllReceivers, localReceiverUuid, allReceivers]);

  // Get receiver location for map centering
  const receiverLocation = useMemo(() => {
    const uuid = showAllReceivers ? allReceivers[0]?.receiverUuid : localReceiverUuid;
    const receiver = allReceivers.find(r => r.receiverUuid === uuid);
    if (receiver && receiver.latitude && receiver.longitude) {
      return [receiver.latitude, receiver.longitude];
    }
    return null;
  }, [showAllReceivers, localReceiverUuid, allReceivers]);

  // Handle local receiver change
  const handleLocalReceiverChange = useCallback((e) => {
    const value = e.target.value;
    setLocalReceiverUuid(value);
    setSelectedAircraft(null);
    if (value !== 'ALL' && onReceiverChange) {
      const receiver = allReceivers.find(r => r.receiverUuid === value);
      if (receiver) onReceiverChange(receiver.receiverName);
    }
  }, [allReceivers, onReceiverChange]);

  // Seed live view with historical messages from database on mount/receiver change
  useEffect(() => {
    if (!effectiveReceiverUuid) return;
    // Only seed once per receiver
    if (seedLoadedRef.current[effectiveReceiverUuid]) return;
    seedLoadedRef.current[effectiveReceiverUuid] = true;

    const fetchSeed = async () => {
      try {
        const token = getAccessToken();
        const today = new Date().toISOString().split('T')[0];
        const res = await getAcarsMessages(effectiveReceiverUuid, today, token, 500);
        if (res?.messages && res.messages.length > 0) {
          // Add receivedAt based on timestamp so the timeout logic works
          const seeded = res.messages.map(msg => ({
            ...msg,
            receiverUuid: msg.receiverUuid || effectiveReceiverUuid,
            receivedAt: msg.timestamp ? parseUtcTimestamp(msg.timestamp) : Date.now(),
            _seed: true, // Mark as seed message for dedup
          }));
          setSeedMessages(prev => {
            // Merge with any existing seeds from other receivers
            const existingIds = new Set(prev.map(m => m.id));
            const newMsgs = seeded.filter(m => !existingIds.has(m.id));
            return [...prev, ...newMsgs];
          });
        }
      } catch (error) {
        console.error('Error fetching seed ACARS messages:', error);
      }
    };

    fetchSeed();
  }, [effectiveReceiverUuid]);

  // Fetch history data when viewing history modes
  useEffect(() => {
    if (viewMode === 'live' || !effectiveReceiverUuid) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const token = getAccessToken();
        const isInteresting = viewMode === 'interesting';

        const [messagesRes, aircraftRes, statsRes] = await Promise.all([
          getAcarsMessages(effectiveReceiverUuid, selectedDate, token, 200, selectedAircraft, isInteresting),
          getAcarsAircraft(effectiveReceiverUuid, selectedDate, token),
          getAcarsStats(effectiveReceiverUuid, selectedDate, token)
        ]);

        if (messagesRes?.messages) setHistoryMessages(messagesRes.messages);
        if (aircraftRes?.aircraft) setAircraftList(aircraftRes.aircraft);
        if (statsRes) setStats(statsRes);
      } catch (error) {
        console.error('Error fetching ACARS data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [viewMode, selectedDate, effectiveReceiverUuid, selectedAircraft]);

  // Filter live messages by selected receiver, merging with seed messages from DB
  const filteredAcarsMessages = useMemo(() => {
    const live = showAllReceivers ? acarsMessages : acarsMessages.filter(msg => msg.receiverUuid === localReceiverUuid);
    if (seedMessages.length === 0) return live;

    // Merge: live messages take priority, dedup by id
    const liveIds = new Set(live.filter(m => m.id).map(m => m.id));
    const filteredSeeds = seedMessages.filter(m => {
      if (m.id && liveIds.has(m.id)) return false; // Already in live
      if (!showAllReceivers && m.receiverUuid !== localReceiverUuid) return false;
      return true;
    });
    if (filteredSeeds.length === 0) return live;

    // Combine and sort by timestamp descending (newest first)
    const combined = [...live, ...filteredSeeds];
    combined.sort((a, b) => {
      const ta = a.receivedAt || parseUtcTimestamp(a.timestamp);
      const tb = b.receivedAt || parseUtcTimestamp(b.timestamp);
      return tb - ta;
    });
    return combined;
  }, [acarsMessages, showAllReceivers, localReceiverUuid, seedMessages]);

  // Derive aircraft from live messages
  const liveAircraft = useMemo(() => {
    if (viewMode !== 'live') return [];
    const now = Date.now();
    const map = new Map();
    filteredAcarsMessages.forEach(msg => {
      if (!msg.address) return;
      // Skip messages older than timeout
      const msgTime = msg.receivedAt || parseUtcTimestamp(msg.timestamp) || now;
      if (msgTime && (now - msgTime) > ACARS_AIRCRAFT_TIMEOUT_MS) return;

      // Clean address for grouping (strip leading dots to avoid duplicates like .CS-TTS and ..CS-TTS)
      const cleanAddr = msg.address.replace(/^\.+/, '');
      if (!cleanAddr) return;

      const existing = map.get(cleanAddr);
      if (!existing) {
        map.set(cleanAddr, {
          address: msg.address, // Keep original for message filtering
          cleanAddress: cleanAddr,
          messageCount: 1,
          lastLabel: msg.label,
          lastLabelDescription: msg.labelDescription,
          hasPosition: msg.hasPosition || false,
          latitude: msg.latitude,
          longitude: msg.longitude,
          flightLevel: msg.flightLevel,
          heading: msg.heading,
          lastPositionTime: msg.hasPosition ? msgTime : 0,
          icao24: msg.icao24,
          lastSeen: msg.timestamp,
          receiverUuid: msg.receiverUuid,
          // Backend enrichment fields (from aircraft_registration DB)
          registration: msg.registration,
          aircraftType: msg.aircraftType,
          ownerOperator: msg.ownerOperator,
          military: msg.military,
          // Flight identification (from ACARS downlink header)
          flight: msg.flight,
          carrierIata: msg.carrierIata,
          flightNumber: msg.flightNumber,
          carrierIcao: msg.carrierIcao,
          carrierName: msg.carrierName,
          // Route data
          originIcao: msg.originIcao,
          destinationIcao: msg.destinationIcao,
          nearestAirportIcao: msg.nearestAirportIcao,
          // OOOI event tracking
          ooiEvents: msg.ooiEvent ? [{ event: msg.ooiEvent, time: msg.timestamp, airport: msg.nearestAirportIcao || msg.originIcao, label: msg.label }] : [],
          // Track all raw address variants for message filtering
          addressVariants: new Set([msg.address]),
        });
      } else {
        existing.messageCount++;
        existing.lastLabel = msg.label;
        existing.lastLabelDescription = msg.labelDescription;
        existing.addressVariants.add(msg.address);
        if (msg.hasPosition) {
          existing.hasPosition = true;
          existing.latitude = msg.latitude;
          existing.longitude = msg.longitude;
          existing.flightLevel = msg.flightLevel;
          existing.lastPositionTime = msgTime;
          if (msg.heading != null) existing.heading = msg.heading;
        }
        if (!existing.icao24 && msg.icao24) existing.icao24 = msg.icao24;
        // Update backend enrichment if newly available
        if (msg.registration && !existing.registration) existing.registration = msg.registration;
        if (msg.aircraftType && !existing.aircraftType) existing.aircraftType = msg.aircraftType;
        if (msg.ownerOperator && !existing.ownerOperator) existing.ownerOperator = msg.ownerOperator;
        if (msg.military != null && existing.military == null) existing.military = msg.military;
        if (msg.flight && !existing.flight) existing.flight = msg.flight;
        if (msg.carrierIata && !existing.carrierIata) existing.carrierIata = msg.carrierIata;
        if (msg.flightNumber && !existing.flightNumber) existing.flightNumber = msg.flightNumber;
        if (msg.carrierIcao && !existing.carrierIcao) existing.carrierIcao = msg.carrierIcao;
        if (msg.carrierName && !existing.carrierName) existing.carrierName = msg.carrierName;
        // Route data
        if (msg.originIcao && !existing.originIcao) existing.originIcao = msg.originIcao;
        if (msg.destinationIcao && !existing.destinationIcao) existing.destinationIcao = msg.destinationIcao;
        if (msg.nearestAirportIcao && !existing.nearestAirportIcao) existing.nearestAirportIcao = msg.nearestAirportIcao;
        // Track OOOI events
        if (msg.ooiEvent) {
          existing.ooiEvents = existing.ooiEvents || [];
          existing.ooiEvents.push({ event: msg.ooiEvent, time: msg.timestamp, airport: msg.nearestAirportIcao || msg.originIcao, label: msg.label });
        }
      }
    });
    return Array.from(map.values()).sort((a, b) => b.messageCount - a.messageCount);
  }, [filteredAcarsMessages, viewMode]);

  // Get the address variants for the selected aircraft (handles .CS-TTS vs ..CS-TTS)
  const selectedAddressVariants = useMemo(() => {
    if (!selectedAircraft) return null;
    const ac = liveAircraft.find(a => a.cleanAddress === selectedAircraft);
    return ac?.addressVariants || new Set([selectedAircraft]);
  }, [selectedAircraft, liveAircraft]);

  // Get displayed messages
  const displayedMessages = useMemo(() => {
    const msgs = viewMode === 'live' ? filteredAcarsMessages : historyMessages;
    if (selectedAircraft && selectedAddressVariants) {
      return msgs.filter(m => selectedAddressVariants.has(m.address) || m.address?.replace(/^\.+/, '') === selectedAircraft);
    }
    return msgs;
  }, [viewMode, filteredAcarsMessages, historyMessages, selectedAircraft, selectedAddressVariants]);

  // Get displayed aircraft list
  const displayedAircraft = useMemo(() => {
    return viewMode === 'live' ? liveAircraft : aircraftList.map(a => ({
      address: a.address,
      messageCount: a.messageCount,
      lastSeen: a.lastSeen,
      positionCount: a.positionCount,
      labels: a.labels,
      hasPosition: a.positionCount > 0,
      icao24: a.icao24
    }));
  }, [viewMode, liveAircraft, aircraftList]);

  // Build reverse registration → aircraft data lookup from ADS-B data
  const regLookupMap = useMemo(() => {
    if (!aircraftData || Object.keys(aircraftData).length === 0) return {};
    const map = {};
    Object.values(aircraftData).forEach(ac => {
      // Backend serializes registration as "r" (via @SerializedName("r"))
      const registration = ac.r || ac.registration;
      if (registration) {
        const reg = registration.toUpperCase();
        const callsign = ac.flight?.trim() || ac.callsign;
        let airlineCode = ac.airline_icao || ac.airlineCode;
        let airlineName = ac.airline_name || ac.airlineName;
        if (!airlineCode && callsign) {
          airlineCode = extractAirlineCode(callsign, { registration, military: ac.military });
        }
        map[reg] = {
          registration,
          callsign,
          aircraftType: ac.t || ac.aircraftType,
          aircraftDescription: ac.desc || ac.aircraftDescription,
          airlineCode,
          airlineName,
          countryCode: ac.country_code || ac.countryCode,
          country: ac.country,
          military: ac.military === true || (ac.category != null && isMilitaryAircraft(ac.category)),
          icao24: ac.hex
        };
      }
    });
    return map;
  }, [aircraftData]);

  // Build enrichment map: backend DB enrichment (primary) + live ADS-B (secondary)
  const enrichmentMap = useMemo(() => {
    const map = {};
    // 1. Backend enrichment from ACARS messages (aircraft_registration DB lookup)
    // This is the primary source — works for all ACARS aircraft, not just those in ADS-B feed
    const allAddresses = new Set();
    const allIcao24s = new Set();
    displayedMessages.forEach(msg => {
      if (msg.icao24) {
        allIcao24s.add(msg.icao24);
        // Backend enrichment: use fields embedded in the message by the backend
        const backendEnriched = enrichFromBackendMsg(msg);
        if (backendEnriched && !map[msg.icao24]) {
          map[msg.icao24] = backendEnriched;
        }
      }
      if (msg.address) allAddresses.add(msg.address);
    });
    // Also check aircraft list for backend enrichment
    displayedAircraft.forEach(ac => {
      if (ac.icao24) {
        allIcao24s.add(ac.icao24);
        if (!map[ac.icao24] && (ac.registration || ac.aircraftType || ac.ownerOperator || ac.carrierIcao)) {
          map[ac.icao24] = {
            registration: ac.registration || null,
            aircraftType: ac.aircraftType || null,
            aircraftDescription: null,
            airlineCode: ac.carrierIcao || null,
            airlineName: ac.carrierName || ac.ownerOperator || null,
            countryCode: null,
            military: ac.military === true,
            flight: ac.flight || null,
            carrierIata: ac.carrierIata || null,
            flightNumber: ac.flightNumber || null,
          };
        }
      }
      if (ac.address) allAddresses.add(ac.address);
    });

    // 2. ADS-B enrichment: merge in live ADS-B data (fills gaps like airline code, callsign)
    if (aircraftData && Object.keys(aircraftData).length > 0) {
      allIcao24s.forEach(icao24 => {
        const adsbEnriched = enrichFromAdsb(icao24, aircraftData);
        if (adsbEnriched) {
          const existing = map[icao24];
          if (!existing) {
            // No backend data — use ADS-B as sole source
            map[icao24] = adsbEnriched;
          } else {
            // Merge: ADS-B fills gaps in backend data (airline code, callsign, description)
            if (!existing.airlineCode && adsbEnriched.airlineCode) existing.airlineCode = adsbEnriched.airlineCode;
            if (!existing.airlineName && adsbEnriched.airlineName) existing.airlineName = adsbEnriched.airlineName;
            if (!existing.callsign && adsbEnriched.callsign) existing.callsign = adsbEnriched.callsign;
            if (!existing.aircraftDescription && adsbEnriched.aircraftDescription) existing.aircraftDescription = adsbEnriched.aircraftDescription;
            if (!existing.countryCode && adsbEnriched.countryCode) existing.countryCode = adsbEnriched.countryCode;
            if (!existing.country && adsbEnriched.country) existing.country = adsbEnriched.country;
          }
        }
      });

      // 3. Registration-based enrichment: match ACARS registration to ADS-B registration.
      // VDL2 AVLC addresses differ from ADS-B Mode-S ICAO addresses, so ICAO-based
      // lookup (step 2) often fails. Registration matching bridges this gap.
      allAddresses.forEach(address => {
        const cleanReg = cleanRegistration(address);
        const regEnriched = enrichFromRegistration(address, regLookupMap);
        if (regEnriched) {
          // Always store under reg: key for lookups
          map[`reg:${cleanReg}`] = regEnriched;
          // Also merge into any existing ICAO-keyed entry for this address
          const icao24 = displayedMessages.find(m => m.address === address && m.icao24)?.icao24 ||
                         displayedAircraft.find(a => a.address === address && a.icao24)?.icao24;
          if (icao24 && map[icao24]) {
            const existing = map[icao24];
            // Merge: ADS-B registration match fills gaps in backend enrichment
            if (!existing.airlineCode && regEnriched.airlineCode) existing.airlineCode = regEnriched.airlineCode;
            if (!existing.airlineName && regEnriched.airlineName) existing.airlineName = regEnriched.airlineName;
            if (!existing.callsign && regEnriched.callsign) existing.callsign = regEnriched.callsign;
            if (!existing.aircraftDescription && regEnriched.aircraftDescription) existing.aircraftDescription = regEnriched.aircraftDescription;
            if (!existing.countryCode && regEnriched.countryCode) existing.countryCode = regEnriched.countryCode;
            if (!existing.country && regEnriched.country) existing.country = regEnriched.country;
          }
        }
      });
    }
    return map;
  }, [aircraftData, displayedMessages, displayedAircraft, regLookupMap]);

  // Aircraft with positions for map (stay visible for 5 minutes after last position)
  const aircraftPositions = useMemo(() => {
    if (viewMode !== 'live') return [];
    const now = Date.now();
    return liveAircraft.filter(a =>
      a.hasPosition && a.latitude && a.longitude &&
      a.lastPositionTime && (now - a.lastPositionTime) < ACARS_POSITION_MAP_TIMEOUT_MS
    );
  }, [liveAircraft, viewMode]);

  // Position trail for selected aircraft (all position messages, oldest → newest)
  const selectedAircraftTrail = useMemo(() => {
    if (!selectedAircraft || !selectedAddressVariants) return [];
    const msgs = viewMode === 'live' ? filteredAcarsMessages : historyMessages;
    return msgs
      .filter(m => (selectedAddressVariants.has(m.address) || m.address?.replace(/^\.+/, '') === selectedAircraft) && m.hasPosition && m.latitude && m.longitude)
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  }, [selectedAircraft, selectedAddressVariants, viewMode, filteredAcarsMessages, historyMessages]);

  // Map tile config
  const tileConfig = useMemo(() => getTileLayerConfig(settings?.mapStyle || 'carto-dark'), [settings?.mapStyle]);
  const mapFilter = useMemo(() => getMapFilter(settings?.mapStyle || 'carto-dark'), [settings?.mapStyle]);

  const handleAircraftClick = useCallback((address, ac) => {
    const cleanAddr = ac?.cleanAddress || address.replace(/^\.+/, '');
    const isDeselecting = selectedAircraft === cleanAddr;
    setSelectedAircraft(prev => prev === cleanAddr ? null : cleanAddr);
    setExpandedMessage(null);
    // Fly to aircraft position if it has one (on select, not deselect)
    if (!isDeselecting && ac?.hasPosition && ac?.latitude && ac?.longitude) {
      setSelectedMessagePos({
        lat: ac.latitude,
        lng: ac.longitude,
        reg: ac.registration || ac.address,
        type: ac.aircraftType,
        time: ac.lastSeen,
        flightLevel: ac.flightLevel,
      });
    } else {
      setSelectedMessagePos(null);
    }
  }, [selectedAircraft]);

  const handleMessageClick = useCallback((msgId, msg) => {
    setExpandedMessage(prev => prev === msgId ? null : msgId);
    // If message has position data, show it on the map
    if (msg?.hasPosition && msg?.latitude && msg?.longitude) {
      setSelectedMessagePos({
        lat: msg.latitude,
        lng: msg.longitude,
        reg: msg.registration || msg.address,
        type: msg.aircraftType,
        time: msg.timestamp,
        flightLevel: msg.flightLevel,
      });
    } else {
      setSelectedMessagePos(null);
    }
  }, []);

  const formatTime = useCallback((timestamp) => {
    if (!timestamp) return '-';
    try {
      const d = new Date(timestamp);
      return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    } catch {
      return String(timestamp).substring(11, 19);
    }
  }, []);

  // Live stats derived from messages
  const liveStats = useMemo(() => {
    if (viewMode !== 'live') return null;
    const total = filteredAcarsMessages.length;
    const withPos = filteredAcarsMessages.filter(m => m.hasPosition).length;
    const textMsgs = filteredAcarsMessages.filter(m => m.isTextMessage).length;
    const uniqueAc = new Set(filteredAcarsMessages.map(m => m.address).filter(Boolean)).size;
    return { totalMessages: total, positions: withPos, textMessages: textMsgs, uniqueAircraft: uniqueAc };
  }, [filteredAcarsMessages, viewMode]);

  const displayStats = viewMode === 'live' ? liveStats : stats;

  // Receiver status summary for "All Receivers"
  const allReceiversStatus = useMemo(() => {
    if (!showAllReceivers) return null;
    const active = allReceivers.filter(r => getStatusClass(r) === 'online').length;
    const connected = allReceivers.filter(r => getStatusClass(r) === 'connected').length;
    const total = allReceivers.length;
    const parts = [];
    if (active > 0) parts.push(`${active} active`);
    if (connected > 0) parts.push(`${connected} connected`);
    if (parts.length === 0) return { status: 'offline', label: `${total} offline` };
    return { status: active > 0 ? 'online' : 'connected', label: parts.join(', ') };
  }, [showAllReceivers, allReceivers]);

  // Column count for colSpan (Text moved to second sub-row)
  const baseColCount = 11; // flag, reg, flight, operator, type, a/g, loc, time, label, freq, crc
  const colCount = baseColCount + (showAllReceivers ? 1 : 0);

  return (
    <div className="messages-page">
      {/* Controls bar — matches ADS-B DashboardInfo layout */}
      <div className="dashboard-info">
        <div className="left-controls">
          <div className="receiver-display">
            <span className="receiver-label">Receiver:</span>
            <select
              className="receiver-select"
              value={localReceiverUuid || ''}
              onChange={handleLocalReceiverChange}
            >
              {allReceivers.length > 1 && (
                <option value="ALL">All Receivers</option>
              )}
              {allReceivers.map(r => (
                <option key={r.receiverUuid} value={r.receiverUuid}>
                  {r.receiverName || r.displayName}
                </option>
              ))}
            </select>
            {/* Status badge — matches ADS-B DashboardInfo style */}
            {showAllReceivers && allReceiversStatus ? (
              <span className={`receiver-status ${allReceiversStatus.status}`}>
                <span className="status-dot"></span>
                <span className="status-label">{allReceiversStatus.label}</span>
              </span>
            ) : selectedReceiver ? (
              <span className={`receiver-status ${getStatusClass(selectedReceiver)}`}>
                <span className="status-dot"></span>
                <span className="status-label">{getStatusLabel(selectedReceiver)}</span>
              </span>
            ) : null}
          </div>
          {/* Auto-zoom — after status on the left */}
          <span className="filter-separator">|</span>
          <span className="filter-label">Auto Zoom:</span>
          <select
            className="auto-zoom-select"
            value={acarsAutoZoom}
            onChange={(e) => { setAcarsAutoZoom(e.target.value); localStorage.setItem('acarsAutoZoom', e.target.value); }}
          >
            <option value="none">None</option>
            <option value="aircraft">Visible Aircraft</option>
            <option value="airport">Airport</option>
          </select>
        </div>
        <div className="right-controls">
          {/* View mode toggle */}
          <div className="messages-view-toggle">
            <button className={viewMode === 'live' ? 'active' : ''} onClick={() => { setViewMode('live'); setSelectedAircraft(null); setSelectedMessagePos(null); }}>
              Live
            </button>
            <button className={viewMode === 'history' ? 'active' : ''} onClick={() => { setViewMode('history'); setSelectedAircraft(null); setSelectedMessagePos(null); }}>
              History
            </button>
            <button className={viewMode === 'interesting' ? 'active' : ''} onClick={() => { setViewMode('interesting'); setSelectedAircraft(null); setSelectedMessagePos(null); }}>
              Interesting
            </button>
          </div>
          {/* Date picker for history modes */}
          {viewMode !== 'live' && (
            <>
              <span className="filter-separator">|</span>
              <span className="filter-label">Day:</span>
              <input
                type="date"
                className="auto-zoom-select"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                style={{ minWidth: '130px' }}
              />
            </>
          )}
        </div>
      </div>

      {/* Main content */}
      <div className="messages-content">
        {/* Left: Map */}
        <div className="messages-map-panel">
          {receiverLocation ? (
            <MapContainer
              center={receiverLocation}
              zoom={7}
              style={{ width: '100%', height: '100%', filter: mapFilter }}
              zoomControl={false}
            >
              <TileLayer {...tileConfig} />
              <AutoZoomController
                mode={acarsAutoZoom}
                receiverLocation={receiverLocation}
                aircraftPositions={aircraftPositions}
                selectedMessagePos={selectedMessagePos}
              />

              {/* Receiver location — antenna icon like ADS-B */}
              <Marker
                position={receiverLocation}
                icon={receiverIcon}
              >
                <Tooltip permanent={false}>
                  {selectedReceiver?.receiverName || allReceivers[0]?.receiverName || 'Receiver'}
                </Tooltip>
              </Marker>

              {/* Selected aircraft position trail (dots + line) */}
              {selectedAircraftTrail.length > 1 && (
                <Polyline
                  positions={selectedAircraftTrail.map(m => [m.latitude, m.longitude])}
                  color="var(--accent-primary, #3b82f6)"
                  weight={2}
                  opacity={0.5}
                  dashArray="4 6"
                />
              )}
              {selectedAircraftTrail.map((m, i) => {
                const isLast = i === selectedAircraftTrail.length - 1;
                if (isLast) return null; // Last position shown as main marker below
                return (
                  <CircleMarker
                    key={`trail-${i}`}
                    center={[m.latitude, m.longitude]}
                    radius={3}
                    fillColor="#3b82f6"
                    fillOpacity={0.3 + (0.5 * i / selectedAircraftTrail.length)}
                    color="#3b82f6"
                    weight={1}
                    opacity={0.5}
                  >
                    <Tooltip permanent={false}>
                      <div>
                        <div>{new Date(m.timestamp).toLocaleTimeString('en-GB')}</div>
                        {m.flightLevel && <div>FL{m.flightLevel}</div>}
                      </div>
                    </Tooltip>
                  </CircleMarker>
                );
              })}

              {/* ACARS aircraft with positions */}
              {aircraftPositions.map(ac => {
                const enriched = enrichmentMap[ac.icao24] || enrichmentMap[`reg:${cleanRegistration(ac.address)}`];
                const reg = enriched?.registration || cleanRegistration(ac.address);
                const isSelected = selectedAircraft === (ac.cleanAddress || ac.address);
                const acType = enriched?.aircraftType || ac.aircraftType;
                const icon = getAcarsAircraftIcon(acType, ac.heading, isSelected);
                return (
                  <Marker
                    key={ac.cleanAddress || ac.address}
                    position={[ac.latitude, ac.longitude]}
                    icon={icon}
                    eventHandlers={{ click: () => handleAircraftClick(ac.address, ac) }}
                  >
                    <Tooltip permanent={isSelected} className="acars-marker-tooltip">
                      <strong>{reg}</strong>
                      {enriched?.aircraftType && <span> · {enriched.aircraftType}</span>}
                      {ac.flightLevel != null && <span> · FL{ac.flightLevel}</span>}
                    </Tooltip>
                    <Popup className="acars-marker-popup" maxWidth={320} minWidth={260} closeButton={true} autoPan={true}>
                      <AcarsAircraftPopup ac={ac} enriched={enriched} />
                    </Popup>
                  </Marker>
                );
              })}

              {/* Selected message position marker (from message table click) */}
              {selectedMessagePos && (
                <>
                  <MapFlyTo position={[selectedMessagePos.lat, selectedMessagePos.lng]} />
                  <Marker
                    position={[selectedMessagePos.lat, selectedMessagePos.lng]}
                    icon={acarsIconSelected}
                  >
                    <Tooltip permanent>
                      <div>
                        <strong>{cleanRegistration(selectedMessagePos.reg)}</strong>
                        {selectedMessagePos.type && <div>{selectedMessagePos.type}</div>}
                        {selectedMessagePos.flightLevel != null && <div>FL{selectedMessagePos.flightLevel}</div>}
                        {selectedMessagePos.time && <div>{new Date(selectedMessagePos.time).toLocaleTimeString('en-GB')}</div>}
                      </div>
                    </Tooltip>
                  </Marker>
                </>
              )}
            </MapContainer>
          ) : (
            <div className="messages-no-map">
              <span>Select a receiver to show map</span>
            </div>
          )}
          {/* Stats overlay on map — same map-hud instrument strip as Live/History */}
          {displayStats && (
            <div className="map-hud">
              <div className="map-hud-item">
                <span className="map-hud-l">Map</span>
                <span className="map-hud-v">{compactNumber(aircraftPositions.length)}</span>
              </div>
              <div className="map-hud-item muted">
                <span className="map-hud-l">No&nbsp;Pos</span>
                <span className="map-hud-v">{compactNumber(Math.max(0, displayedAircraft.length - aircraftPositions.length))}</span>
              </div>
              <div className="map-hud-item">
                <span className="map-hud-l">Msgs</span>
                <span className="map-hud-v">{compactNumber(displayStats.totalMessages || 0)}</span>
              </div>
              <div className="map-hud-item">
                <span className="map-hud-l">Acft</span>
                <span className="map-hud-v">{compactNumber(displayStats.uniqueAircraft || displayedAircraft.length || 0)}</span>
              </div>
              <div className="map-hud-item">
                <span className="map-hud-l">Pos</span>
                <span className="map-hud-v">{compactNumber(displayStats.positions || 0)}</span>
              </div>
              {displayStats.crcRate != null && displayStats.crcRate <= 100 && (
                <div className="map-hud-item">
                  <span className="map-hud-l">CRC</span>
                  <span className="map-hud-v">{displayStats.crcRate.toFixed(0)}%</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right: Aircraft list + Messages table */}
        <div className="messages-data-panel">
          {/* Aircraft list (top half) */}
          <div className="messages-aircraft-list">
            <table className="messages-aircraft-table">
              <thead>
                <tr>
                  <th className="col-flag"></th>
                  <th>Reg</th>
                  <th>Flight</th>
                  <th>Operator</th>
                  <th>Type</th>
                  <th className="col-status">A/G</th>
                  <th className="col-status" title="Has position data">Loc</th>
                  <th className="col-numeric">Msgs</th>
                  <th>Last Label</th>
                  <th>Last Seen</th>
                </tr>
              </thead>
              <tbody>
                {displayedAircraft.length === 0 ? (
                  <tr><td colSpan="10" style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '16px' }}>
                    {loading ? 'Loading...' : viewMode === 'live' ? 'Waiting for ACARS/VDL2 messages...' : 'No aircraft found'}
                  </td></tr>
                ) : (
                  displayedAircraft.map(ac => {
                    const enriched = enrichmentMap[ac.icao24] || enrichmentMap[`reg:${cleanRegistration(ac.address)}`];
                    const countryCode = enriched?.countryCode || getCountryFromRegistration(ac.address || enriched?.registration) || getCountryFromHex(ac.icao24);
                    const reg = enriched?.registration || cleanRegistration(ac.address);
                    return (
                      <tr
                        key={ac.cleanAddress || ac.address}
                        className={`${selectedAircraft === (ac.cleanAddress || ac.address) ? 'selected' : ''} ${ac.hasPosition ? 'has-position' : ''}`}
                        onClick={() => handleAircraftClick(ac.address, ac)}
                      >
                        <td className="col-flag"><FlagIcon countryCode={countryCode} /></td>
                        <td className="reg-cell">{reg}</td>
                        <td className="flight-cell">{ac.flight || enriched?.flight || '-'}</td>
                        <td className="operator-cell">
                          {enriched ? (
                            enriched.military ? (
                              <span className="military-badge">MIL</span>
                            ) : enriched.airlineCode ? (
                              <AirlineLogo airlineCode={enriched.airlineCode} airlineName={enriched.airlineName} />
                            ) : enriched.airlineName ? (
                              <span className="airline-text" title={enriched.airlineName}>
                                {enriched.airlineName.length > 16 ? enriched.airlineName.substring(0, 16) + '…' : enriched.airlineName}
                              </span>
                            ) : '-'
                          ) : '-'}
                        </td>
                        <td>
                          {enriched?.aircraftType ? (
                            <AircraftTypeDisplay aircraftType={enriched.aircraftType} iconSize={12} />
                          ) : '-'}
                        </td>
                        <td className="col-status"><AirGroundIcon status={getAircraftAirGroundStatus(ac, enriched)} /></td>
                        <td className="col-status"><LocationIcon hasPosition={ac.hasPosition} title={ac.hasPosition ? `${ac.latitude?.toFixed(4)}, ${ac.longitude?.toFixed(4)}` : undefined} /></td>
                        <td className="col-numeric">{ac.messageCount}</td>
                        <td className="label-cell">{ac.lastLabel ? <><span className="label-code">{ac.lastLabel}</span>{ac.lastLabelDescription && <span className="label-desc"> {ac.lastLabelDescription}</span>}</> : ac.labels ? <span className="label-code">{ac.labels.split(',')[0].trim()}</span> : '-'}</td>
                        <td>{formatTime(ac.lastSeen)}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Message table (bottom half) */}
          <div className="messages-table-container" ref={tableContainerRef}>
            {selectedAircraft && (() => {
              const selAc = displayedAircraft.find(a => (a.cleanAddress || a.address) === selectedAircraft);
              const ooiEvents = selAc?.ooiEvents || [];
              return (
                <>
                  <div className="selected-aircraft-header">
                    <span>Messages for: <strong>{selectedAircraft}</strong>
                      {selAc?.originIcao && selAc?.destinationIcao && (
                        <span style={{ fontWeight: 400, marginLeft: 8 }}>{selAc.originIcao} → {selAc.destinationIcao}</span>
                      )}
                    </span>
                    <button
                      className="close-filter-btn"
                      onClick={(e) => { e.stopPropagation(); setSelectedAircraft(null); setSelectedMessagePos(null); }}
                      title="Show all messages"
                    >✕ Show All</button>
                  </div>
                  {ooiEvents.length > 0 && (
                    <div className="oooi-timeline">
                      {ooiEvents.sort((a, b) => new Date(a.time) - new Date(b.time)).map((evt, i) => (
                        <span key={i} className={`oooi-event ${evt.event?.toLowerCase()}`}>
                          <span className="oooi-event-label">{evt.event === 'OUT' ? 'Gate OUT' : evt.event === 'OFF' ? 'Wheels OFF' : evt.event === 'ON' ? 'Wheels ON' : evt.event === 'IN' ? 'Gate IN' : evt.event}</span>
                          <span className="oooi-event-time">{evt.time ? new Date(evt.time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : ''}</span>
                          {evt.airport && <span className="oooi-event-airport">{evt.airport}</span>}
                        </span>
                      ))}
                    </div>
                  )}
                </>
              );
            })()}
            <table className="messages-table">
              <thead>
                <tr>
                  <th className="col-flag"></th>
                  <th>Reg</th>
                  <th>Flight</th>
                  <th>Operator</th>
                  <th>Type</th>
                  <th className="col-status">A/G</th>
                  <th className="col-status" title="Has position data">Loc</th>
                  <th>Time</th>
                  {showAllReceivers && <th>Receiver</th>}
                  <th>Label</th>
                  <th>Freq</th>
                  <th>CRC</th>
                </tr>
              </thead>
              <tbody>
                {displayedMessages.length === 0 ? (
                  <tr><td colSpan={colCount} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '24px' }}>
                    {loading ? 'Loading messages...' : viewMode === 'live' ? 'Waiting for ACARS/VDL2 messages...' : 'No messages found'}
                  </td></tr>
                ) : (
                  displayedMessages.map((msg, idx) => {
                    const msgKey = msg.id || `${msg.timestamp}-${msg.address}-${idx}`;
                    const isExpanded = expandedMessage === msgKey;
                    const enriched = enrichmentMap[msg.icao24] || enrichmentMap[`reg:${cleanRegistration(msg.address)}`];
                    const countryCode = enriched?.countryCode || getCountryFromRegistration(msg.address || enriched?.registration) || getCountryFromHex(msg.icao24);
                    const reg = enriched?.registration || cleanRegistration(msg.address);

                    return (
                      <React.Fragment key={msgKey}>
                        <tr
                          className={`${!msg.crcValid ? 'crc-fail' : ''} ${isExpanded ? 'expanded' : ''} ${msg.hasPosition ? 'has-position' : ''}`}
                          onClick={() => handleMessageClick(msgKey, msg)}
                        >
                          <td className="col-flag" data-label-none><FlagIcon countryCode={countryCode} /></td>
                          <td className="reg-cell" data-label="Reg">{reg}</td>
                          <td className="flight-cell" data-label="Flight">{msg.flight || enriched?.flight || '-'}</td>
                          <td className="operator-cell" data-label="Operator">
                            {enriched ? (
                              enriched.military ? (
                                <span className="military-badge">MIL</span>
                              ) : enriched.airlineCode ? (
                                <AirlineLogo airlineCode={enriched.airlineCode} airlineName={enriched.airlineName} />
                              ) : enriched.airlineName ? (
                                <span className="airline-text" title={enriched.airlineName}>
                                  {enriched.airlineName.length > 16 ? enriched.airlineName.substring(0, 16) + '…' : enriched.airlineName}
                                </span>
                              ) : '-'
                            ) : '-'}
                          </td>
                          <td data-label="Type">
                            {enriched?.aircraftType ? (
                              <AircraftTypeDisplay aircraftType={enriched.aircraftType} iconSize={10} />
                            ) : '-'}
                          </td>
                          <td className="col-status" data-label="A/G"><AirGroundIcon status={getAirGroundStatus(msg, enriched)} /></td>
                          <td className="col-status" data-label="Loc"><LocationIcon hasPosition={msg.hasPosition} title={msg.hasPosition ? `${msg.latitude?.toFixed(4)}, ${msg.longitude?.toFixed(4)}` : undefined} /></td>
                          <td className="col-time" data-label="Time">{formatTime(msg.timestamp)}</td>
                          {showAllReceivers && (
                            <td className="receiver-cell" data-label="Receiver">{receiverNameMap[msg.receiverUuid] || '-'}</td>
                          )}
                          <td className="label-cell" data-label="Label">{msg.label ? <><span className="label-code">{msg.label}</span>{msg.labelDescription && <span className="label-desc"> {msg.labelDescription}</span>}</> : '-'}</td>
                          <td data-label="Freq" title={getFrequencyTooltip(msg.frequencyMhz)}>{msg.frequencyMhz ? msg.frequencyMhz.toFixed(3) : '-'}</td>
                          <td data-label="CRC">
                            <span className={`crc-badge ${msg.crcValid ? 'valid' : 'invalid'}`}>
                              {msg.crcValid ? 'OK' : 'FAIL'}
                            </span>
                          </td>
                        </tr>
                        {/* Second sub-row: message text + decoded summary */}
                        <MessageSubRow msg={msg} colCount={colCount} onClick={() => handleMessageClick(msgKey, msg)} />
                        {isExpanded && (
                          <tr key={`${msgKey}-detail`} className="message-expanded-row">
                            <td colSpan={colCount}>
                              <MessageDetail msg={msg} receiverName={receiverNameMap[msg.receiverUuid]} enriched={enriched} />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Second sub-row showing message text + decoded summary inline */
function MessageSubRow({ msg, colCount, onClick }) {
  // Parse decoded content for summary
  const decodedSummary = useMemo(() => {
    if (!msg.decodedContent) return null;
    try {
      const lines = JSON.parse(msg.decodedContent);
      if (!lines || lines.length === 0) return null;
      // Show first 2-3 decoded lines as a compact summary
      return lines.slice(0, 3);
    } catch {
      return null;
    }
  }, [msg.decodedContent]);

  const hasText = msg.text && msg.text.trim().length > 0;
  const hasDecoded = decodedSummary && decodedSummary.length > 0;

  // Don't render sub-row if no content at all
  if (!hasText && !hasDecoded) return null;

  return (
    <tr className="message-subrow" onClick={onClick}>
      <td colSpan={colCount}>
        <div className="message-subrow-content">
          {hasDecoded && (
            <span className="subrow-decoded">
              {decodedSummary.map((line, i) => (
                <span key={i} className="subrow-decoded-item">{line}{i < decodedSummary.length - 1 ? '  ·  ' : ''}</span>
              ))}
            </span>
          )}
          {hasText && !hasDecoded && (
            <span className="subrow-text">{msg.text.length > 120 ? msg.text.substring(0, 120) + '…' : msg.text}</span>
          )}
          {hasText && hasDecoded && (
            <span className="subrow-text"> — {msg.text.length > 80 ? msg.text.substring(0, 80) + '…' : msg.text}</span>
          )}
        </div>
      </td>
    </tr>
  );
}

/** Expanded message detail view */
function MessageDetail({ msg, receiverName, enriched }) {
  const decodedLines = useMemo(() => {
    if (!msg.decodedContent) return null;
    try {
      return JSON.parse(msg.decodedContent);
    } catch {
      return null;
    }
  }, [msg.decodedContent]);

  const msgType = getMessageType(msg.frequencyMhz);
  const countryCode = enriched?.countryCode || getCountryFromRegistration(msg.address || enriched?.registration) || getCountryFromHex(msg.icao24);

  return (
    <div className="message-detail">
      <div className="message-detail-header">
        {countryCode && <FlagIcon countryCode={countryCode} />}
        <span><strong>{enriched?.registration || cleanRegistration(msg.address)}</strong></span>
        {enriched?.aircraftType && <span>Type: {enriched.aircraftType} {enriched.aircraftDescription ? `(${enriched.aircraftDescription})` : ''}</span>}
        {msg.flight && <span>Flight: {msg.flight}{msg.carrierName ? ` (${msg.carrierName})` : ''}</span>}
        {enriched?.airlineName && !msg.carrierName && <span>Operator: {enriched.airlineName}</span>}
        {enriched?.callsign && <span>Callsign: {enriched.callsign}</span>}
        <span className="detail-separator">|</span>
        <span>Mode: {msg.mode || '-'}</span>
        <span>Block: {msg.blockId || '-'}</span>
        <span>ICAO24: {msg.icao24 || '-'}</span>
        <span>Type: {msgType}</span>
        {receiverName && <span>Receiver: {receiverName}</span>}
        {msg.frequencyMhz && <span>Freq: {msg.frequencyMhz.toFixed(3)} MHz</span>}
        {msg.signalLevel != null && <span>Signal: {msg.signalLevel.toFixed(1)} dBFS</span>}
        {msg.hasPosition && <span>Lat: {msg.latitude?.toFixed(4)} Lon: {msg.longitude?.toFixed(4)}</span>}
        {msg.flightLevel && <span>FL{msg.flightLevel}</span>}
        {msg.mach && <span>M{msg.mach.toFixed(2)}</span>}
      </div>

      {/* Raw message data */}
      {msg.text && (
        <div className="message-raw-data">
          <span className="raw-data-label">Raw Data</span>
          <pre className="raw-data-content">{msg.text}</pre>
        </div>
      )}

      {/* Decoded content */}
      {decodedLines && decodedLines.length > 0 && (
        <div className="message-decoded-content">
          <h4>Decoded</h4>
          <ul>
            {decodedLines.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default Messages;
